/**
 * Ícono del tipo de una tarea, como insignia (estilo Jira): requerimiento = marcador, bug =
 * insecto, deuda técnica = engranaje, consulta = globo con «?». La forma distingue el tipo, no
 * solo el color. Los colores salen de las variables `--tipo-*` (estilos/tokens.css).
 *
 * Con `decorativo` se oculta a los lectores de pantalla: úsalo donde el nombre del tipo ya está
 * escrito al lado.
 */
import { ETIQUETA_TIPO, type Tipo } from '@cdtec-backlog/contrato';
import type { ReactNode } from 'react';
import './IconoTipo.css';

const CLASE: Record<Tipo, string> = {
  REQUERIMIENTO: 'requerimiento',
  ERROR: 'bug',
  DEUDA_TECNICA: 'deuda',
  CONSULTA: 'consulta',
};

const DIBUJO: Record<Tipo, ReactNode> = {
  REQUERIMIENTO: <path className="icono-tipo__relleno" d="M4.5 2.8h7v10.4L8 10.4l-3.5 2.8z" />,
  ERROR: (
    <>
      <ellipse className="icono-tipo__relleno" cx="8" cy="9.2" rx="3" ry="3.6" />
      <circle className="icono-tipo__relleno" cx="8" cy="4.6" r="1.7" />
      <path
        className="icono-tipo__trazo"
        d="M5 7.4 2.8 6.3M5 9.6H2.5M5.2 11.8l-2 1.4M11 7.4l2.2-1.1M11 9.6h2.5M10.8 11.8l2 1.4"
      />
    </>
  ),
  DEUDA_TECNICA: (
    <>
      <circle className="icono-tipo__trazo icono-tipo__trazo--grueso" cx="8" cy="8" r="2.7" />
      <path
        className="icono-tipo__trazo"
        d="M8 2.2v2.2M8 11.6v2.2M2.2 8h2.2M11.6 8h2.2M3.9 3.9l1.5 1.5M10.6 10.6l1.5 1.5M3.9 12.1l1.5-1.5M10.6 5.4l1.5-1.5"
      />
    </>
  ),
  CONSULTA: (
    <>
      <path
        className="icono-tipo__relleno"
        d="M3.8 2.5h8.4a1 1 0 0 1 1 1v6.6a1 1 0 0 1-1 1H8.4L5.2 13.6v-2.5H3.8a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1z"
      />
      <path
        className="icono-tipo__hueco icono-tipo__hueco--trazo"
        d="M6.5 5.5a1.5 1.5 0 1 1 2.3 1.2c-.5.3-.8.6-.8 1.2"
      />
      <circle className="icono-tipo__hueco" cx="8" cy="9.4" r="0.75" />
    </>
  ),
};

interface Props {
  tipo: Tipo;
  decorativo?: boolean;
}

export function IconoTipo({ tipo, decorativo = false }: Props) {
  const nombre = ETIQUETA_TIPO[tipo];
  return (
    <svg
      className={`icono-tipo icono-tipo--${CLASE[tipo]}`}
      viewBox="0 0 16 16"
      width="16"
      height="16"
      focusable="false"
      {...(decorativo ? { 'aria-hidden': true } : { role: 'img', 'aria-label': `Tipo: ${nombre}` })}
    >
      {!decorativo && <title>{`Tipo: ${nombre}`}</title>}
      {DIBUJO[tipo]}
    </svg>
  );
}
